import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';

const RelatedArticles = () => {
  const relatedArticles = [
  {
    id: 1,
    title: "Développement des Compétences IA : Guide Pratique 2025",
    excerpt: "Un parcours structuré pour développer vos compétences en Intelligence Artificielle et rester compétitif.",
    author: "Prof. Amadou Diallo",
    readTime: "12 min",
    category: "Développement des Compétences",
    categoryColor: "green",
    publishedDate: "5 Nov 2025",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_184610a40-1767082357474.png",
    alt: "Groupe de professionnels africains en formation sur l'intelligence artificielle avec des ordinateurs portables et des écrans interactifs",
    slug: "/skills-development-ai-guide",
    trending: true
  },
  {
    id: 2,
    title: "Machine Learning pour les PME Africaines",
    excerpt: "Comment les petites et moyennes entreprises peuvent tirer parti du machine learning pour optimiser leurs opérations.",
    author: "Dr. Fatima Ndiaye",
    readTime: "6 min",
    category: "Intelligence Artificielle",
    categoryColor: "purple",
    publishedDate: "4 Nov 2025",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10205f4b5-1767082355746.png",
    alt: "Écran d'ordinateur montrant des graphiques de machine learning et des données analytiques pour les entreprises africaines",
    slug: "/machine-learning-pme-africaines",
    popular: true
  },
  {
    id: 3,
    title: "Transformation Digitale : Cas d\'Étude Réussi",
    excerpt: "Analyse détaillée d\'une transformation digitale réussie dans une entreprise manufacturière ivoirienne.",
    author: "M. David Ouattara",
    readTime: "12 min",
    category: "Transformation Digitale",
    categoryColor: "blue",
    publishedDate: "1 Nov 2025",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f489626b-1767082356903.png",
    alt: "Usine moderne en Côte d\'Ivoire avec des écrans numériques et des systèmes automatisés illustrant la transformation digitale",
    slug: "/transformation-digitale-cas-etude",
    recommended: true
  }];


  const getCategoryColorClasses = (color) => {
    const colorMap = {
      purple: "bg-purple-100 text-purple-800",
      green: "bg-green-100 text-green-800",
      blue: "bg-blue-100 text-blue-800",
      red: "bg-red-100 text-red-800"
    };
    return colorMap?.[color] || "bg-gray-100 text-gray-800";
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 text-orange-800 text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            Articles Recommandés
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Continuez Votre Lecture
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Approfondissez vos connaissances avec ces articles sélectionnés sur l'IA et le développement des compétences
          </p>
        </div>

        {/* Related Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {relatedArticles?.map((article) =>
          <Link
            key={article?.id}
            to={article?.slug}
            className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-purple-200">

              {/* Article Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                src={article?.image}
                alt={article?.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 rounded-md text-xs font-semibold ${getCategoryColorClasses(article?.categoryColor)}`}>
                    {article?.category}
                  </span>
                </div>

                {/* Special Badges */}
                <div className="absolute top-3 right-3">
                  {article?.trending &&
                <span className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded-md mb-2 block">
                      🔥 Tendance
                    </span>
                }
                  {article?.popular &&
                <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded-md mb-2 block">
                      ⭐ Populaire
                    </span>
                }
                  {article?.recommended &&
                <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-md">
                      ✨ Recommandé
                    </span>
                }
                </div>

                {/* Read Time */}
                <div className="absolute bottom-3 right-3">
                  <span className="px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-md">
                    {article?.readTime}
                  </span>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <span>{article?.publishedDate}</span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors duration-300 line-clamp-2">
                  {article?.title}
                </h3>

                <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3">
                  {article?.excerpt}
                </p>

                {/* Author & CTA */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-xs">
                        {article?.author?.split(' ')?.map((name) => name?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{article?.author}</p>
                    </div>
                  </div>

                  <div className="flex items-center text-purple-600 text-sm group-hover:text-purple-700">
                    <span className="mr-1">Lire</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16">
          <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 rounded-2xl p-8 text-center text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M20%2020.5V18H0v-2h20v2.5zm-.5%200h-2v-2H20v2zm-.5%200h-2v-2H20v2z%22/%3E%3C/g%3E%3C/svg%3E')]"></div>
            </div>

            <div className="relative">
              <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Ne Manquez Aucun Article
              </h3>
              <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
                Recevez nos derniers insights sur l&apos;IA, les cas d&apos;études exclusifs et les guides pratiques 
                directement dans votre boîte mail.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  className="flex-1 px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />

                <button className="px-8 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 whitespace-nowrap">
                  S'abonner
                </button>
              </div>

              <p className="text-blue-200 text-sm mt-4">
                Gratuit • 2,500+ abonnés • Désabonnement facile
              </p>
            </div>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="text-center mt-12">
          <Link
            to="/blog-hub"
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl">

            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
            Voir Tous les Articles
          </Link>
        </div>
      </div>
    </section>);

};

export default RelatedArticles;